/**
 * Timer Tab - Configure capture intervals and schedules
 * Phase 3: Scheduled Capture + Foreground Service
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  Switch,
  Select,
  InputNumber,
  Space,
  Typography,
  Button,
  Alert,
  Tag,
  message,
  Segmented,
  Modal,
  Card,
} from 'antd';
import { View, TimerSettings, TimeRange, DaySchedule } from '@/types';
import { MaterialIcon } from '@/components/MaterialIcon';
import { SectionLabel } from '@/components/SectionLabel';
import { FieldLabel } from '@/components/FieldLabel';
import { SECTION_GAP, BORDER, RADIUS, SECONDARY } from '@/styles/design-tokens';

const { Text } = Typography;
const { Option } = Select;

interface TimerTabProps {
  view: View;
  onSave: (capturing: boolean, timerSettings?: TimerSettings) => Promise<void>;
  onSaveHandlerReady?: (handleSave: () => Promise<void>, hasChanges: boolean, handleCancel: () => void) => void;
  hideSaveButton?: boolean;
  highlightFieldId?: string | null;
  onHighlightComplete?: () => void;
}

export interface TimerTabHandle {
  handleSave: () => Promise<void>;
  saving: boolean;
  disabled: boolean;
}

// Time unit options for custom interval
const TIME_UNITS = [
  { label: 'seconds', value: 1 },
  { label: 'minutes', value: 60 },
  { label: 'hours', value: 3600 },
];

// Days of week for badge selector (Sunday first, JS standard)
const DAYS_OF_WEEK = [
  { label: 'Sunday', short: 'SUN', value: 0 },
  { label: 'Monday', short: 'MON', value: 1 },
  { label: 'Tuesday', short: 'TUE', value: 2 },
  { label: 'Wednesday', short: 'WED', value: 3 },
  { label: 'Thursday', short: 'THU', value: 4 },
  { label: 'Friday', short: 'FRI', value: 5 },
  { label: 'Saturday', short: 'SAT', value: 6 },
];

// Days order for Advanced mode UI (Monday → Sunday, European standard)
const DAY_ORDER_UI = [1, 2, 3, 4, 5, 6, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun

const DAY_NAMES: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

// Generate time options with 10-minute intervals (HH:mm format)
const generateTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 10) {
      const h = hour.toString().padStart(2, '0');
      const m = minute.toString().padStart(2, '0');
      options.push(`${h}:${m}`);
    }
  }
  // Add 24:00 as last option (end of day)
  options.push('24:00');
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Convert HH:mm to minutes for comparison
const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

// Check if a time range is valid (start < end)
const isValidTimeRange = (start: string, end: string): boolean => {
  return timeToMinutes(start) < timeToMinutes(end);
};

// Structural equality for the fields a save actually writes.
function sameTimerSettings(a: TimerSettings, b: TimerSettings): boolean {
  return a.interval === b.interval
    && a.scheduleEnabled === b.scheduleEnabled
    && (a.scheduleMode ?? 'basic') === (b.scheduleMode ?? 'basic')
    && JSON.stringify(a.daysOfWeek ?? null) === JSON.stringify(b.daysOfWeek ?? null)
    && JSON.stringify(a.timeRanges ?? null) === JSON.stringify(b.timeRanges ?? null)
    && JSON.stringify(a.dailySchedules ?? null) === JSON.stringify(b.dailySchedules ?? null);
}

// Check if all time ranges in an array are valid
const hasInvalidTimeRanges = (ranges: TimeRange[]): boolean => {
  return ranges.some(r => !isValidTimeRange(r.start, r.end));
};

/** Every day of the week, in the order the schedule fields use. */
const ALL_DAYS = [0, 1, 2, 3, 4, 5, 6];

/** A range that spans the whole day places no restriction on capture. */
const isFullDay = (ranges: TimeRange[]): boolean =>
  ranges.length === 1 && timeToMinutes(ranges[0].start) === 0 && timeToMinutes(ranges[0].end) === 1440;

/**
 * Whether the schedule fields actually restrict anything.
 *
 * Derived rather than toggled: "all seven days, 00:00-24:00" and "no schedule" are
 * the same instruction written two ways, and a separate flag let them disagree —
 * ranges were editable and saved while the flag stayed false, so a window the user
 * had built was silently ignored by the device. Narrow the hours and the schedule
 * applies; widen them back to the full week and it stops.
 */
function deriveScheduleEnabled(
  scheduleMode: 'basic' | 'advanced',
  selectedDays: number[],
  timeRanges: TimeRange[],
  dailySchedules: DaySchedule[],
): boolean {
  if (scheduleMode === 'advanced') {
    const active = dailySchedules.filter(d => d.enabled);
    if (active.length < ALL_DAYS.length) return true;
    return active.some(d => !isFullDay(d.timeRanges));
  }
  if (selectedDays.length < ALL_DAYS.length) return true;
  return !isFullDay(timeRanges);
}

// Helper to compute initial interval value and unit from settings.
// Returns null when the view has no configuration: an unconfigured view must read
// as unconfigured, not as some plausible-looking number. Showing a default here is
// what let the dashboard claim "1m" while the device captured every 5 minutes.
function computeIntervalFromSettings(settings?: TimerSettings): { value: number; unit: number } | null {
  if (!settings?.interval) return null;

  const interval = settings.interval;
  if (interval % 3600 === 0) return { value: interval / 3600, unit: 3600 };
  if (interval % 60 === 0) return { value: interval / 60, unit: 60 };
  return { value: interval, unit: 1 };
}

export function TimerTab({ view, onSave, onSaveHandlerReady, hideSaveButton, highlightFieldId, onHighlightComplete }: TimerTabProps) {
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  // Initialize all state directly from view to avoid flash of default values
  const initialInterval = computeIntervalFromSettings(view.timerSettings);
  // Form controls need a number to render; this placeholder is never persisted —
  // an unconfigured view is flagged by `isUnconfigured` and saving writes whatever
  // the user actually picks.
  const initialIntervalOrPlaceholder = initialInterval ?? { value: 5, unit: 60 };
  const isUnconfigured = !view.timerSettings;
  const [capturing, setCapturing] = useState(view.capturing || false);
  const [togglingCapture, setTogglingCapture] = useState(false);
  const [customValue, setCustomValue] = useState<number>(initialIntervalOrPlaceholder.value);
  const [customUnit, setCustomUnit] = useState<number>(initialIntervalOrPlaceholder.unit);
  const [selectedDays, setSelectedDays] = useState<number[]>(
    view.timerSettings?.daysOfWeek || [0, 1, 2, 3, 4, 5, 6]
  );
  const [timeRanges, setTimeRanges] = useState<TimeRange[]>(
    view.timerSettings?.timeRanges?.length ? view.timerSettings.timeRanges : [{ start: '00:00', end: '24:00' }]
  );
  // Advanced mode state
  const [scheduleMode, setScheduleMode] = useState<'basic' | 'advanced'>(
    view.timerSettings?.scheduleMode || 'basic'
  );
  const [dailySchedules, setDailySchedules] = useState<DaySchedule[]>(
    view.timerSettings?.dailySchedules || []
  );

  // Not state: it follows from the days and ranges above, so editing them is what
  // turns the schedule on. It used to be state with no control to set it.
  const scheduleEnabled = deriveScheduleEnabled(scheduleMode, selectedDays, timeRanges, dailySchedules);

  // Track if user has made changes to prevent useEffect from resetting state
  const [userHasEdited, setUserHasEdited] = useState(false);


  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  // Save carries the current capture state but must not depend on it: capturing is
  // applied the moment it is toggled, from here or from the app, and a toggle is not
  // a pending edit. Reading it through a ref keeps handleSave's identity stable, so
  // a toggle no longer re-runs the effect that reports "unsaved changes" upwards.
  const capturingRef = useRef(capturing);
  capturingRef.current = capturing;

  // The settings object the last successful save wrote. Firestore echoes it back a
  // moment later; until it does, `view.timerSettings` still holds the previous
  // values, and re-hydrating the form from them would resurrect the old baseline and
  // leave Save armed for changes the user had just persisted.
  const lastSavedSettingsRef = useRef<TimerSettings | null>(null);

  // Track initial form state to detect changes
  const initialStateRef = useRef<{
    capturing: boolean;
    customValue: number;
    customUnit: number;
    selectedDays: number[];
    timeRanges: TimeRange[];
    scheduleMode: 'basic' | 'advanced';
    dailySchedules: DaySchedule[];
  } | null>(null);

  // Initialize form from view data (but not if user has made changes)
  // IMPORTANT: Only depend on view.capturing and view.timerSettings, NOT the entire view object
  // androidStatus changes frequently (heartbeats) and would cause excessive re-renders
  const viewCapturing = view.capturing;
  const viewTimerSettings = view.timerSettings;

  // Switching view discards any pending echo: what we saved belongs to the previous
  // one, and holding it would keep this form from tracking the new view's document.
  const viewIdRef = useRef(view.viewId);
  if (viewIdRef.current !== view.viewId) {
    viewIdRef.current = view.viewId;
    lastSavedSettingsRef.current = null;
  }

  // Capturing follows Firestore unconditionally: it is live device state, not a form
  // field, so it must keep tracking the app's toggle even while the schedule fields
  // below hold unsaved edits. Kept apart from the form-hydrating effect for exactly
  // that reason — that one bails out on unsaved edits, which would freeze the pill.
  useEffect(() => {
    setCapturing(viewCapturing || false);
    if (initialStateRef.current) {
      initialStateRef.current.capturing = viewCapturing || false;
    }
  }, [viewCapturing]);

  useEffect(() => {

    // Skip initialization if user has made changes (unsaved edits)
    if (userHasEdited || hasChanges()) {
      return;
    }

    // Skip while Firestore has not caught up with our own save: re-seeding the form
    // from the stale document would undo what was just written and re-arm Save.
    const lastSaved = lastSavedSettingsRef.current;
    if (lastSaved) {
      if (!viewTimerSettings || !sameTimerSettings(viewTimerSettings, lastSaved)) {
        return;
      }
      lastSavedSettingsRef.current = null; // echo received; resume tracking Firestore
    }

    let newCustomValue = 5;
    let newCustomUnit = 60;
    let newSelectedDays = [0, 1, 2, 3, 4, 5, 6];
    let newTimeRanges: TimeRange[] = [{ start: '00:00', end: '24:00' }];
    let newScheduleMode: 'basic' | 'advanced' = 'basic';
    let newDailySchedules: DaySchedule[] = [];

    if (viewTimerSettings) {
      const settings = viewTimerSettings;

      // Convert interval to best unit
      if (settings.interval % 3600 === 0) {
        newCustomValue = settings.interval / 3600;
        newCustomUnit = 3600;
        setCustomValue(newCustomValue);
        setCustomUnit(newCustomUnit);
      } else if (settings.interval % 60 === 0) {
        newCustomValue = settings.interval / 60;
        newCustomUnit = 60;
        setCustomValue(newCustomValue);
        setCustomUnit(newCustomUnit);
      } else {
        newCustomValue = settings.interval;
        newCustomUnit = 1;
        setCustomValue(newCustomValue);
        setCustomUnit(newCustomUnit);
      }

      // Load schedule mode
      newScheduleMode = settings.scheduleMode || 'basic';
      setScheduleMode(newScheduleMode);

      // Read back whatever was stored, without gating on scheduleEnabled: the form
      // lets these be edited either way, so it must show what is actually saved.
      newSelectedDays = settings.daysOfWeek || [0, 1, 2, 3, 4, 5, 6];
      setSelectedDays(newSelectedDays);

      if (settings.timeRanges && settings.timeRanges.length > 0) {
        newTimeRanges = settings.timeRanges;
        setTimeRanges(newTimeRanges);
      }

      // Load daily schedules for advanced mode
      if (settings.dailySchedules && settings.dailySchedules.length > 0) {
        newDailySchedules = settings.dailySchedules;
        setDailySchedules(newDailySchedules);
      }
    }

    // Store initial state for comparison (deep copy arrays/objects to avoid mutation issues)
    initialStateRef.current = {
      capturing: capturingRef.current,
      customValue: newCustomValue,
      customUnit: newCustomUnit,
      selectedDays: [...newSelectedDays],
      timeRanges: newTimeRanges.map(r => ({ ...r })),
      scheduleMode: newScheduleMode,
      dailySchedules: newDailySchedules.map(d => ({
        day: d.day,
        enabled: d.enabled,
        timeRanges: d.timeRanges.map(r => ({ ...r })),
      })),
    };
  // Deliberately NOT keyed on view.capturing: this effect rebuilds the form's baseline,
  // and a capture toggle — ours or the app's — changes no form field. Re-running it on
  // every toggle made Save light up for an edit nobody had made.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- hasChanges uses state internally
  }, [viewTimerSettings, userHasEdited]);

  // Calculate current interval in seconds
  const getCurrentInterval = (): number => {
    return customValue * customUnit;
  };

  const getMinInterval = (): { min: number; reason: string } => {
    const isDslrRawEnabled = view.settings?.camera?.dslr?.rawEnabled === true
      || view.androidStatus?.device?.dslr?.cameraRawEnabled === true;
    const internalHdr = view.settings?.camera?.internal?.hdrMode;
    const isHdrActive = internalHdr != null && internalHdr !== 'off';
    if (isDslrRawEnabled) return { min: 30, reason: 'RAW capture requires a minimum interval of 30 seconds' };
    if (isHdrActive) return { min: 15, reason: 'HDR requires a minimum interval of 15 seconds' };
    return { min: 15, reason: 'Minimum interval is 15 seconds' };
  };

  // Check if current form state differs from initial state
  const hasChanges = (): boolean => {
    if (!initialStateRef.current) return false;

    const initial = initialStateRef.current;

    // Capturing is deliberately excluded: it is applied the moment it is toggled,
    // so it is never a pending change waiting for Save.
    if (customValue !== initial.customValue) return true;
    if (customUnit !== initial.customUnit) return true;
    if (scheduleMode !== initial.scheduleMode) return true;

    // Check selectedDays array
    if (selectedDays.length !== initial.selectedDays.length) return true;
    if (!selectedDays.every(day => initial.selectedDays.includes(day))) return true;

    // Check timeRanges array
    if (timeRanges.length !== initial.timeRanges.length) return true;
    for (let i = 0; i < timeRanges.length; i++) {
      if (timeRanges[i].start !== initial.timeRanges[i].start) return true;
      if (timeRanges[i].end !== initial.timeRanges[i].end) return true;
    }

    // Check dailySchedules array (for advanced mode)
    if (dailySchedules.length !== initial.dailySchedules.length) return true;
    for (let i = 0; i < dailySchedules.length; i++) {
      const current = dailySchedules[i];
      const initialDay = initial.dailySchedules.find(d => d.day === current.day);
      if (!initialDay) return true;
      if (current.enabled !== initialDay.enabled) return true;
      if (current.timeRanges.length !== initialDay.timeRanges.length) return true;
      for (let j = 0; j < current.timeRanges.length; j++) {
        if (current.timeRanges[j].start !== initialDay.timeRanges[j]?.start) return true;
        if (current.timeRanges[j].end !== initialDay.timeRanges[j]?.end) return true;
      }
    }

    return false;
  };

  // Reset form to initial values
  const handleCancel = useCallback(() => {
    if (!initialStateRef.current) return;

    const initial = initialStateRef.current;

    setCapturing(initial.capturing);
    setCustomValue(initial.customValue);
    setCustomUnit(initial.customUnit);
    setSelectedDays(initial.selectedDays);
    setTimeRanges(initial.timeRanges);
    setScheduleMode(initial.scheduleMode);
    setDailySchedules(initial.dailySchedules);

    // Reset dirty flag
    setUserHasEdited(false);
  }, []);

  // Calculate storage estimate
  const getStorageEstimate = () => {
    const interval = getCurrentInterval();
    if (interval < 10) return null; // Invalid

    // Calculate photos per day
    let photosPerDay = 86400 / interval; // 24h = 86400 seconds

    // Adjust for schedule
    if (scheduleEnabled) {
      // Convert time ranges to minutes and sort by start time
      const rangesInMinutes = timeRanges.map(range => {
        const [startHour, startMin] = range.start.split(':').map(Number);
        let [endHour, endMin] = range.end.split(':').map(Number);
        // Handle 24:00 as end of day (1440 minutes)
        if (endHour === 24) {
          endHour = 24;
          endMin = 0;
        }
        return {
          start: startHour * 60 + startMin,
          end: endHour * 60 + endMin,
        };
      }).sort((a, b) => a.start - b.start);

      // Merge overlapping ranges
      const mergedRanges: { start: number; end: number }[] = [];
      for (const range of rangesInMinutes) {
        if (mergedRanges.length === 0) {
          mergedRanges.push({ ...range });
        } else {
          const lastRange = mergedRanges[mergedRanges.length - 1];
          if (range.start <= lastRange.end) {
            // Overlapping or adjacent - merge
            lastRange.end = Math.max(lastRange.end, range.end);
          } else {
            // Non-overlapping - add new range
            mergedRanges.push({ ...range });
          }
        }
      }

      // Calculate total active minutes from merged ranges
      const totalActiveMinutes = mergedRanges.reduce((total, range) => {
        return total + (range.end - range.start);
      }, 0);

      const activeHours = totalActiveMinutes / 60;
      photosPerDay = (activeHours * 3600) / interval;

      // Adjust for days
      if (selectedDays.length < 7) {
        photosPerDay = (photosPerDay * selectedDays.length) / 7;
      }
    }

    // Estimate storage (assume 3MB avg per photo)
    const photosPerMonth = photosPerDay * 30;
    const storagePerMonthMB = photosPerMonth * 3;
    const storagePerMonthGB = storagePerMonthMB / 1024;

    return {
      photosPerDay: Math.round(photosPerDay),
      photosPerMonth: Math.round(photosPerMonth),
      storageGB: storagePerMonthGB.toFixed(2),
    };
  };

  /**
   * Start/stop capture takes effect immediately — it is a device command, not a form
   * edit, so it must not sit behind Save alongside the schedule parameters. The pill
   * flips optimistically and rolls back if the write fails.
   */
  const handleCapturingToggle = useCallback(async (val: boolean) => {
    const previous = capturing;
    setCapturing(val);
    setTogglingCapture(true);
    try {
      // Persist capturing alone: passing no settings leaves the schedule untouched,
      // so unsaved edits in the form below are neither published nor discarded.
      await onSaveRef.current(val);
      if (initialStateRef.current) {
        initialStateRef.current.capturing = val;
      }
    } catch (error) {
      console.error('[TimerTab] Failed to toggle capturing:', error);
      setCapturing(previous);
      message.error('Could not change capture state');
    } finally {
      setTogglingCapture(false);
    }
  }, [capturing]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);

      let interval = customValue * customUnit;
      console.log('[TimerTab] Saving - customValue:', customValue, 'customUnit:', customUnit, 'interval:', interval);

      const { min: minInterval, reason: minIntervalReason } = getMinInterval();
      if (interval < minInterval) {
        form.setFields([
          {
            name: 'customValue',
            errors: [minIntervalReason],
          },
        ]);
        return;
      }

      // Validate time ranges
      if (scheduleMode === 'basic' && hasInvalidTimeRanges(timeRanges)) {
        message.error('Invalid time range: start time must be before end time');
        return;
      }

      if (scheduleMode === 'advanced') {
        const invalidDay = dailySchedules.find(d => d.enabled && hasInvalidTimeRanges(d.timeRanges));
        if (invalidDay) {
          message.error(`Invalid time range on ${DAY_NAMES[invalidDay.day]}: start time must be before end time`);
          return;
        }
      }

      // Days and time ranges are persisted whenever the form offers them, regardless
      // of scheduleEnabled. Gating them on it dropped edits the UI had just accepted:
      // deselecting a day changed the form, was never written, and left Save armed
      // over a difference that no save could resolve.
      const settings: TimerSettings = {
        interval,
        scheduleEnabled,
        scheduleMode,
        ...(selectedDays.length > 0 && { daysOfWeek: selectedDays }),
        ...(timeRanges.length > 0 && { timeRanges }),
        ...(scheduleMode === 'advanced' && dailySchedules.length > 0 && { dailySchedules }),
      };

      const capturingNow = capturingRef.current;
      console.log('[TimerTab] Timer settings to save:', settings);
      console.log('[TimerTab] capturing:', capturingNow);
      await onSaveRef.current(capturingNow, settings);

      lastSavedSettingsRef.current = settings;

      // Success: Show toast
      message.success('Timer settings saved successfully');

      // Update initialStateRef with the new saved values so hasChanges() returns false
      initialStateRef.current = {
        capturing: capturingRef.current,
        customValue,
        customUnit,
        selectedDays: [...selectedDays],
        timeRanges: timeRanges.map(r => ({ ...r })),
        scheduleMode,
        dailySchedules: dailySchedules.map(d => ({
          day: d.day,
          enabled: d.enabled,
          timeRanges: d.timeRanges.map(r => ({ ...r })),
        })),
      };

      // Reset the dirty flag to allow future Firestore updates when they match our saved value
      setUserHasEdited(false);
    } catch (error) {
      message.error('Failed to save timer settings. Please try again.', 5);

      console.error('[TimerTab] Save failed:', error);

      // Don't reset userHasEdited - keep form in dirty state
    } finally {
      setSaving(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- onSave and capturing are read via refs, on purpose
  }, [customValue, customUnit, scheduleEnabled, selectedDays, timeRanges, scheduleMode, dailySchedules, form]);

  // Notify parent when save handler is ready or when form state changes
  // Pass hasChanges() result as boolean so parent re-renders when it changes
  const hasChangesValue = hasChanges();
  useEffect(() => {
    if (onSaveHandlerReady) {
      onSaveHandlerReady(handleSave, hasChangesValue, handleCancel);
    }
  }, [handleSave, handleCancel, onSaveHandlerReady, hasChangesValue]);

  // Handle highlight scroll + flash animation
  const highlightInProgressRef = useRef(false);
  useEffect(() => {
    if (highlightFieldId && !highlightInProgressRef.current) {
      highlightInProgressRef.current = true;
      let attempts = 0;
      const maxAttempts = 10;

      // Retry finding the element until it's found or max attempts reached
      const findAndHighlight = () => {
        const element = document.querySelector(`[data-highlight-id="${highlightFieldId}"]`);
        if (element) {
          // Scroll into view
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Apply flash after scroll completes
          setTimeout(() => {
            element.classList.add('highlight-flash');

            // Listen for animation end instead of using timeout
            const handleAnimationEnd = () => {
              element.classList.remove('highlight-flash');
              element.removeEventListener('animationend', handleAnimationEnd);
              highlightInProgressRef.current = false;
              onHighlightComplete?.();
            };
            element.addEventListener('animationend', handleAnimationEnd);
          }, 300); // Wait for scroll
        } else if (attempts < maxAttempts) {
          // Element not found yet, retry after a short delay
          attempts++;
          setTimeout(findAndHighlight, 50);
        } else {
          // Element not found after max attempts, clear highlight state
          highlightInProgressRef.current = false;
          onHighlightComplete?.();
        }
      };

      // Initial delay to allow drawer animation to start
      setTimeout(findAndHighlight, 200);
    }
  }, [highlightFieldId, onHighlightComplete]);

  const estimate = getStorageEstimate();


  // Handle schedule mode change (Basic ↔ Advanced)
  const handleScheduleModeChange = (newMode: 'basic' | 'advanced') => {
    if (newMode === 'advanced' && scheduleMode === 'basic') {
      // Basic → Advanced: copy timeRanges to all active days
      const newDailySchedules: DaySchedule[] = DAY_ORDER_UI.map(day => ({
        day,
        enabled: selectedDays.includes(day),
        timeRanges: selectedDays.includes(day)
          ? timeRanges.map(r => ({ ...r }))
          : [{ start: '00:00', end: '24:00' }],
      }));
      setDailySchedules(newDailySchedules);
      setScheduleMode('advanced');
      setUserHasEdited(true);
    } else if (newMode === 'basic' && scheduleMode === 'advanced') {
      // Advanced → Basic: check if all days have same ranges
      const enabledDays = dailySchedules.filter(d => d.enabled);

      if (enabledDays.length === 0) {
        // No days enabled, just switch
        setScheduleMode('basic');
        setUserHasEdited(true);
        return;
      }

      const firstDayRanges = JSON.stringify(enabledDays[0].timeRanges);
      const allSameRanges = enabledDays.every(d =>
        JSON.stringify(d.timeRanges) === firstDayRanges
      );

      if (allSameRanges) {
        // All same, convert silently
        convertToBasicMode();
      } else {
        // Different ranges, show warning
        Modal.confirm({
          title: 'Different time ranges detected',
          content: 'Days have different time ranges. Switching to Basic mode will use the ranges from the first active day. Continue?',
          okText: 'Switch to Basic',
          cancelText: 'Cancel',
          onOk: () => convertToBasicMode(),
        });
      }
    }
  };

  // Convert from Advanced to Basic mode
  const convertToBasicMode = () => {
    const enabledDays = dailySchedules.filter(d => d.enabled);
    const newSelectedDays = enabledDays.map(d => d.day);
    const newTimeRanges = enabledDays[0]?.timeRanges || [{ start: '00:00', end: '24:00' }];

    setSelectedDays(newSelectedDays);
    setTimeRanges(newTimeRanges.map(r => ({ ...r })));
    setScheduleMode('basic');
    setUserHasEdited(true);
  };

  // Toggle day enabled/disabled in Advanced mode
  const toggleDayEnabled = (day: number, enabled: boolean) => {
    setDailySchedules(prev =>
      prev.map(d =>
        d.day === day
          ? { ...d, enabled, timeRanges: enabled && d.timeRanges.length === 0 ? [{ start: '00:00', end: '24:00' }] : d.timeRanges }
          : d
      )
    );
    setUserHasEdited(true);
  };

  // Update time ranges for a specific day in Advanced mode
  const updateDayTimeRanges = (day: number, newRanges: TimeRange[]) => {
    setDailySchedules(prev =>
      prev.map(d =>
        d.day === day ? { ...d, timeRanges: newRanges } : d
      )
    );
    setUserHasEdited(true);
  };

  // Add time range to a specific day
  const addDayTimeRange = (day: number) => {
    setDailySchedules(prev =>
      prev.map(d =>
        d.day === day
          ? { ...d, timeRanges: [...d.timeRanges, { start: '00:00', end: '24:00' }] }
          : d
      )
    );
    setUserHasEdited(true);
  };

  // Remove time range from a specific day
  const removeDayTimeRange = (day: number, rangeIndex: number) => {
    setDailySchedules(prev =>
      prev.map(d =>
        d.day === day
          ? { ...d, timeRanges: d.timeRanges.filter((_, i) => i !== rangeIndex) }
          : d
      )
    );
    setUserHasEdited(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <Form form={form} layout="vertical">
        {/* Capturing status pill — mirrors the Share dialog pattern */}
        <div
          data-highlight-id="capturing"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            border: BORDER,
            borderRadius: RADIUS,
            background: capturing ? '#f6ffed' : '#fafafa',
            transition: 'background 180ms ease',
          }}
        >
          <div>
            <div style={{ fontSize: 15, fontWeight: 600 }}>
              {capturing ? 'Capturing enabled' : 'Capturing paused'}
            </div>
            <div style={{ fontSize: 12, color: SECONDARY, marginTop: 2 }}>
              {capturing
                ? 'The device is taking photos on the schedule below.'
                : 'No photos are being taken. Enable to resume capture.'}
            </div>
          </div>
          <Switch
            checked={capturing}
            loading={togglingCapture}
            onChange={handleCapturingToggle}
            disabled={saving || togglingCapture}
          />
        </div>

        {/* Heads the fields Save governs. The pill above is a device command that
            applies immediately, so it sits outside this section. */}
        <div style={{ marginTop: SECTION_GAP }}>
          <SectionLabel>Timer settings</SectionLabel>
        </div>

        {/* Interval — compact field, separate from the status pill */}
        <div data-highlight-id="interval">
          <FieldLabel>Interval</FieldLabel>
          {isUnconfigured && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 8 }}
              message="This view has no timer configuration yet"
              description="The device will not capture until you save an interval here. The value shown below is a starting point, not the device's current setting."
            />
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Space.Compact className="interval-compact" style={{ width: 240 }}>
              <InputNumber
                min={1}
                max={999}
                value={customValue}
                onChange={(val) => {
                  console.log('[TimerTab] InputNumber onChange - new value:', val, 'old value:', customValue);
                  setCustomValue(val || 1);
                  setUserHasEdited(true);
                }}
                style={{ width: 120 }}
                size="large"
                disabled={saving}
              />
              <Select
                value={customUnit}
                onChange={(val) => {
                  setCustomUnit(val);
                  setUserHasEdited(true);
                }}
                style={{ width: 120 }}
                size="large"
                disabled={saving}
              >
                {TIME_UNITS.map(unit => (
                  <Option key={unit.value} value={unit.value}>
                    {unit.label}
                  </Option>
                ))}
              </Select>
            </Space.Compact>

            {/* Speed test hidden — replaced by inline interval warnings */}
          </div>
          {getCurrentInterval() < getMinInterval().min && (
            <Alert
              type="error"
              showIcon
              style={{ marginTop: 8 }}
              message={getMinInterval().reason}
            />
          )}
          {getCurrentInterval() >= getMinInterval().min && getCurrentInterval() < 60 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 8 }}
              message="With intervals below 60 seconds, capture timing may be irregular depending on camera transfer speed and available bandwidth."
            />
          )}
        </div>

        <div style={{ marginTop: SECTION_GAP }}>
          <SectionLabel>Schedule</SectionLabel>
        </div>

        {/* Schedule Mode Toggle */}
        <div style={{ marginBottom: 20 }}>
          <FieldLabel>Schedule mode</FieldLabel>
          <Segmented
            className="segmented-emphasis"
            value={scheduleMode}
            onChange={(val) => handleScheduleModeChange(val as 'basic' | 'advanced')}
            disabled={saving}
            options={[
              { label: 'Basic', value: 'basic' },
              { label: 'Advanced', value: 'advanced' },
            ]}
          />
        </div>

        {/* Basic Mode UI */}
        {scheduleMode === 'basic' && (
          <>
            {/* Days of Week */}
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>Days of week</FieldLabel>
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  flexWrap: 'nowrap',
                  pointerEvents: saving ? 'none' : 'auto',
                  opacity: saving ? 0.6 : 1,
                }}
              >
                <Tag.CheckableTagGroup
                  multiple
                  options={DAYS_OF_WEEK.map(day => ({
                    label: day.short,
                    value: day.value,
                  }))}
                  value={selectedDays}
                  onChange={(values) => {
                    setSelectedDays(values as number[]);
                    setUserHasEdited(true);
                  }}
                  style={{ display: 'flex', gap: 4 }}
                />
              </div>
            </div>

            {/* Time Ranges (shared) */}
            <div style={{ marginBottom: 20 }}>
              <FieldLabel>Time ranges</FieldLabel>
              <Space direction="vertical" style={{ width: '100%' }} size={12}>
                {timeRanges.map((range, index) => {
                  const isInvalid = !isValidTimeRange(range.start, range.end);
                  return (
                    <div key={index}>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <FieldLabel style={{ fontSize: 11, marginBottom: 4 }}>Start time</FieldLabel>
                            <Select
                              value={range.start}
                              onChange={(value) => {
                                const newRanges = timeRanges.map((r, i) =>
                                  i === index ? { ...r, start: value } : { ...r }
                                );
                                setTimeRanges(newRanges);
                                setUserHasEdited(true);
                              }}
                              style={{ width: '100%' }}
                              size="middle"
                              showSearch
                              disabled={saving}
                              status={isInvalid ? 'error' : undefined}
                            >
                              {TIME_OPTIONS.map(time => (
                                <Option key={`start-${index}-${time}`} value={time}>
                                  {time}
                                </Option>
                              ))}
                            </Select>
                          </div>
                          <div>
                            <FieldLabel style={{ fontSize: 11, marginBottom: 4 }}>End time</FieldLabel>
                            <Select
                              value={range.end}
                              onChange={(value) => {
                                const newRanges = timeRanges.map((r, i) =>
                                  i === index ? { ...r, end: value } : { ...r }
                                );
                                setTimeRanges(newRanges);
                                setUserHasEdited(true);
                              }}
                              style={{ width: '100%' }}
                              size="middle"
                              showSearch
                              disabled={saving}
                              status={isInvalid ? 'error' : undefined}
                            >
                              {TIME_OPTIONS.map(time => (
                                <Option key={`end-${index}-${time}`} value={time}>
                                  {time}
                                </Option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <Button
                          size="middle"
                          danger
                          icon={<MaterialIcon name="delete" />}
                          disabled={timeRanges.length === 1 || saving}
                          onClick={() => {
                            const newRanges = timeRanges
                              .filter((_, i) => i !== index)
                              .map(r => ({ ...r }));
                            setTimeRanges(newRanges);
                            setUserHasEdited(true);
                          }}
                        />
                      </div>
                      {isInvalid && (
                        <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                          Start time must be before end time
                        </Text>
                      )}
                    </div>
                  );
                })}
                <Button
                  className="btn-add-ghost"
                  icon={<MaterialIcon name="add" size={16} />}
                  onClick={() => {
                    setTimeRanges([...timeRanges, { start: '00:00', end: '24:00' }]);
                    setUserHasEdited(true);
                  }}
                  size="small"
                  disabled={saving}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Add time range
                </Button>
              </Space>
            </div>
          </>
        )}

        {/* Advanced Mode UI - Per-day schedules */}
        {scheduleMode === 'advanced' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {DAY_ORDER_UI.map(dayNum => {
              const daySchedule = dailySchedules.find(d => d.day === dayNum);
              const isEnabled = daySchedule?.enabled ?? false;
              const dayRanges = daySchedule?.timeRanges ?? [{ start: '00:00', end: '24:00' }];

              return (
                <Card
                  key={dayNum}
                  size="small"
                  style={{
                    opacity: saving ? 0.6 : 1,
                    backgroundColor: isEnabled ? '#fff' : '#fafafa',
                    border: BORDER,
                    borderRadius: RADIUS,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: isEnabled ? 16 : 0 }}>
                    <Switch
                      checked={isEnabled}
                      onChange={(checked) => toggleDayEnabled(dayNum, checked)}
                      disabled={saving}
                      size="small"
                    />
                    <Text strong style={{ minWidth: 80 }}>{DAY_NAMES[dayNum]}</Text>
                    {!isEnabled && (
                      <Text type="secondary" style={{ fontSize: 12 }}>Disabled</Text>
                    )}
                  </div>

                  {isEnabled && (
                    <Space direction="vertical" style={{ width: '100%' }} size={12}>
                      {dayRanges.map((range, rangeIndex) => {
                        const isInvalid = !isValidTimeRange(range.start, range.end);
                        return (
                          <div key={rangeIndex}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                              <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                <div>
                                  <FieldLabel style={{ fontSize: 11, marginBottom: 4 }}>Start time</FieldLabel>
                                  <Select
                                    value={range.start}
                                    onChange={(value) => {
                                      const newRanges = dayRanges.map((r, i) =>
                                        i === rangeIndex ? { ...r, start: value } : r
                                      );
                                      updateDayTimeRanges(dayNum, newRanges);
                                    }}
                                    style={{ width: '100%' }}
                                    size="middle"
                                    showSearch
                                    disabled={saving}
                                    status={isInvalid ? 'error' : undefined}
                                  >
                                    {TIME_OPTIONS.map(time => (
                                      <Option key={`day-${dayNum}-start-${rangeIndex}-${time}`} value={time}>
                                        {time}
                                      </Option>
                                    ))}
                                  </Select>
                                </div>
                                <div>
                                  <FieldLabel style={{ fontSize: 11, marginBottom: 4 }}>End time</FieldLabel>
                                  <Select
                                    value={range.end}
                                    onChange={(value) => {
                                      const newRanges = dayRanges.map((r, i) =>
                                        i === rangeIndex ? { ...r, end: value } : r
                                      );
                                      updateDayTimeRanges(dayNum, newRanges);
                                    }}
                                    style={{ width: '100%' }}
                                    size="middle"
                                    showSearch
                                    disabled={saving}
                                    status={isInvalid ? 'error' : undefined}
                                  >
                                    {TIME_OPTIONS.map(time => (
                                      <Option key={`day-${dayNum}-end-${rangeIndex}-${time}`} value={time}>
                                        {time}
                                      </Option>
                                    ))}
                                  </Select>
                                </div>
                              </div>
                              <Button
                                size="middle"
                                danger
                                icon={<MaterialIcon name="delete" />}
                                disabled={dayRanges.length === 1 || saving}
                                onClick={() => removeDayTimeRange(dayNum, rangeIndex)}
                              />
                            </div>
                            {isInvalid && (
                              <Text type="danger" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
                                Start time must be before end time
                              </Text>
                            )}
                          </div>
                        );
                      })}
                      <Button
                        className="btn-add-ghost"
                        icon={<MaterialIcon name="add" size={16} />}
                        onClick={() => addDayTimeRange(dayNum)}
                        size="small"
                        disabled={saving}
                        style={{ alignSelf: 'flex-start' }}
                      >
                        Add time range
                      </Button>
                    </Space>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Storage Estimation */}
        <div style={{ marginTop: SECTION_GAP }}>
          <SectionLabel>Storage estimation</SectionLabel>
        </div>
        {estimate && (
          <div
            style={{
              padding: 16,
              border: BORDER,
              borderRadius: RADIUS,
              background: '#fff',
            }}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', rowGap: 10, columnGap: 16, alignItems: 'baseline' }}>
              <Text style={{ color: SECONDARY, fontSize: 12 }}>Photos / day</Text>
              <Text style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                ~{estimate.photosPerDay.toLocaleString()}
              </Text>
              <Text style={{ color: SECONDARY, fontSize: 12 }}>Photos / month</Text>
              <Text style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                ~{estimate.photosPerMonth.toLocaleString()}
              </Text>
              <Text style={{ color: SECONDARY, fontSize: 12 }}>Storage / month</Text>
              <Text style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 15, fontWeight: 600, color: '#1f2937' }}>
                ~{estimate.storageGB} GB
              </Text>
            </div>
            <Button
              type="link"
              style={{ padding: 0, height: 'auto', marginTop: 12, fontSize: 13 }}
              onClick={() => navigate('/plans')}
            >
              View storage plans
            </Button>
          </div>
        )}

        {!estimate && (
          <Alert
            type="error"
            message="Minimum interval is 10 seconds"
            showIcon
          />
        )}

        {/* Action Buttons - hide if parent will render them */}
        {!hideSaveButton && (
          <div style={{ marginTop: SECTION_GAP, marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }} size={12}>
              <Button
                type="text"
                onClick={handleCancel}
                disabled={!hasChanges() || saving}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                className="btn-primary-emphasis"
                onClick={handleSave}
                loading={saving}
                disabled={(!estimate && capturing) || !hasChanges()}
              >
                Save timer settings
              </Button>
            </Space>
          </div>
        )}
      </Form>
    </div>
  );
}
